import { formatDateUtc } from '../i18n';
import { FlightTeam } from '../model';
import moment from 'moment';

export type FlightTeamNameInfo = Pick<
    FlightTeam,
    | 'iataFlightIdentifier'
    | 'departureAirportIata'
    | 'arrivalAirportIata'
    | 'scheduledDepartureTime'
    | 'scheduledDepartureTimeUtc'
>;

export const formatFlightTime = (time?: string, isUtc?: boolean) => {
    if (!time) {
        return 'n/a';
    }
    return `${formatDateUtc(time)}${!!isUtc ? ' (UTC)' : ''}`;
};

export const formatFlightTeamTime = (
    time: string | undefined,
    timeUtc: string
) => {
    return time ? formatFlightTime(time) : formatFlightTime(timeUtc, true);
};

export const formatFlightTeamName = (item: FlightTeamNameInfo) => {
    return `${item.iataFlightIdentifier} ${item.departureAirportIata} - ${
        item.arrivalAirportIata
    } ${formatFlightTeamTime(item.scheduledDepartureTime, item.scheduledDepartureTimeUtc)}`;
};

export const formatElapsedMilliseconds = (ms: number): string => {
    if (ms <= 0) return '0m';

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
        const remainingHours = hours % 24;
        return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }

    if (hours > 0) {
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0
            ? `${hours}h ${remainingMinutes}m`
            : `${hours}h`;
    }

    if (minutes > 0) {
        if (minutes >= 5) {
            return `${minutes}m`;
        } else {
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        }
    }
    return `${seconds}s`;
};

export const formatCompactDateTime = (
    dateString: string | null | undefined
): string => {
    if (!dateString) return '-';
    return `${moment.utc(dateString).format('DD/MM HH:mm')} (UTC)`;
};

const numberThresholds: readonly { threshold: number; unit: string }[] = [
    { threshold: 1_000_000_000, unit: 'B' },
    { threshold: 1_000_000, unit: 'M' },
    { threshold: 1_000, unit: 'K' },
] as const;

export const formatIntShort = (value: number): string => {
    for (const x of numberThresholds) {
        const { threshold, unit } = x;
        if (value > threshold) {
            const numberStr = (value / threshold)
                .toFixed(1)
                .replace(/\.0$/, '');
            return `${numberStr} ${unit}`;
        }
    }

    return value.toString();
};

export const formatRating = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return '-';
    }
    return value.toFixed(2);
};

export const formatPercentage = (val: number) => `${val.toFixed(2)}%`;
